import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import client from "../lib/api";
import { fileUrl } from "../lib/api";
import { AdSense } from "./AdSense";

// Maps our internal ad slot_id -> Google AdSense ad-unit ID.
// Leave a value empty ("") to serve a responsive auto unit until a
// dedicated AdSense unit is created for that placement.
const ADSENSE_UNITS = {};

const AdsCtx = createContext({});
export function AdsProvider({ children }) {
  const [ads, setAds] = useState({});
  useEffect(() => {
    client.get("/ads").then(({ data }) => {
      const map = {};
      data.forEach((s) => (map[s.slot_id] = s));
      setAds(map);
    }).catch(() => {});
  }, []);
  return <AdsCtx.Provider value={ads}>{children}</AdsCtx.Provider>;
}

// `house` = { href, img, alt, testid } renders a temporary in-house banner that
// links out. Google AdSense always gets first priority for the slot; the house
// creative only shows if AdSense does not fill (unfilled / not yet approved).
export function AdSlot({ id, className = "", height = 90, house = null }) {
  const { pathname } = useLocation();
  const isFantasy = /^\/fantasy(\/|$)/.test(pathname);
  const ads = useContext(AdsCtx);
  const slot = ads[id];
  const sold = slot && (slot.status === "sold" || slot.status === "flying") && slot.advertiser;
  const wrapRef = useRef(null);
  const [showHouse, setShowHouse] = useState(false);

  useEffect(() => {
    if (isFantasy || sold || !house) return;
    const t = setTimeout(() => {
      const ins = wrapRef.current?.querySelector("ins.adsbygoogle");
      const status = ins?.getAttribute("data-ad-status");
      if (!status || status === "unfilled") setShowHouse(true);
    }, 2500);
    return () => clearTimeout(t);
  }, [sold, house, isFantasy]);

  // Fantasy is ad-free: never mount any slot (or adsbygoogle) on /fantasy*
  if (isFantasy) return null;

  return (
    <div className={`w-full ${className}`} data-testid={`ad-${id}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-ink/40 font-bold mb-1">
        {sold ? "Platform partner" : "Advertisement"}
      </div>
      {sold ? (
        <div
          className="w-full flex items-center justify-center rounded-sm text-center bg-green text-cream"
          style={{ minHeight: height }}
        >
          {slot.creative && slot.creative_type === "image"
            ? <img src={fileUrl(slot.creative) || slot.creative} alt={slot.advertiser} className="max-h-full object-contain" />
            : <span className="font-head font-bold px-4">{slot.advertiser}</span>}
        </div>
      ) : house ? (
        <div className="relative w-full overflow-hidden rounded-sm border border-pslborder" style={{ minHeight: height }}>
          <div ref={wrapRef} style={{ display: showHouse ? "none" : "block" }}>
            <AdSense
              slot={ADSENSE_UNITS[id] || ""}
              style={{ display: "block", minHeight: height }}
              testId={`adsense-${id}`}
            />
          </div>
          {showHouse && (
            house.href ? (
              <a
                href={house.href}
                target="_blank"
                rel="noopener sponsored"
                data-testid={house.testid || `house-ad-${id}`}
                className="block transition-opacity hover:opacity-90"
              >
                <img src={house.img} alt={house.alt} className="w-full h-auto object-cover" loading="lazy" />
              </a>
            ) : house.video ? (
              <video
                data-testid={house.testid || `house-ad-${id}`}
                src={house.video}
                className="w-full h-auto object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster={house.poster}
              />
            ) : (
              <img data-testid={house.testid || `house-ad-${id}`} src={house.img} alt={house.alt} className="w-full h-auto object-cover" loading="lazy" />
            )
          )}
        </div>
      ) : (
        <div className="w-full overflow-hidden rounded-sm" style={{ minHeight: height }}>
          <AdSense
            slot={ADSENSE_UNITS[id] || ""}
            style={{ display: "block", minHeight: height }}
            testId={`adsense-${id}`}
          />
        </div>
      )}
    </div>
  );
}
